import moment, { Moment } from 'moment';
import React, { useMemo, useState } from 'react';
import { Alert, DatePicker, Empty, Select, Spin } from 'antd';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useQuery } from 'react-query';
import styled from 'styled-components';

import { ApiClient } from '../../api';
import { DrawerLayout } from '../../Layout';
import { convertNumberToCommaString } from '../../utils/helper';
import styles from './styles';
import { LineItem, UserStatisticsResponse, ValueItem } from '../../api/types/userStatistics';
import { useRootStatistics } from '../../utils/hooks/useRootStatistics';
import { getUserStatisticsView } from '../../utils/rootStatistics';
import { buildRootActiveUserTrend, ROOT_ACTIVE_SIGNUPS_START } from '../../utils/rootActiveUsers';

const UserInsightsWrapper = styled.div`
    ${styles}
`;

const { RangePicker } = DatePicker;

const palette = ['#1E87F0', '#FF8743', '#2CCC71', '#9B51E0', '#F2994A', '#00A6A6', '#EB5757', '#07004D'];

const hiddenMetrics = new Set(['dormantusers', 'reactivatedusers', 'totalusers']);
const isVisibleMetric = (item: ValueItem) =>
    !hiddenMetrics.has((item.key || item.label || '').replace(/[^a-zA-Z]/g, '').toLowerCase());

interface SeriesConfig {
    dataKey: string;
    name: string;
    stroke: string;
    missingValue: number | null;
}

interface NormalizedLineGroup {
    rows: any[];
    series: SeriesConfig[];
}

const lineGroups = [
    { key: 'summary', label: 'Daily User Trends' },
    { key: 'gameParticipation', label: 'Game Participation' },
    { key: 'signupsByTrackerPlatform', label: 'Signups by Tracker Platform' },
    { key: 'totalUsersByProvider', label: 'Users by Provider' },
] as const;

const safeNumber = (value: any) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatNumber = (value: any) => convertNumberToCommaString(safeNumber(value));

const formatName = (value?: string | null) => {
    const name = String(value || 'untracked').trim() || 'untracked';
    return name
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const rootActiveDescription =
    'Illustrative monthly active population from prior-month retention and actual signup cohorts, not measured unique users. Daily chart dips do not change the monthly carry-forward.';

const getMetricLabel = (item: ValueItem, fallback?: string) => {
    const key = (item.key || item.label || '').replace(/[^a-zA-Z]/g, '').toLowerCase();
    if (key === 'activeusers') {
        return 'Active Users';
    }
    if (key === 'userssignedup') {
        return 'Total Signups';
    }
    return formatName(item.label || item.key || fallback);
};

const getSeriesKey = (value: string | undefined, index: number) => {
    const slug = String(value || `series_${index}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return `series_${slug || index}_${index}`;
};

const formatDate = (value?: string) => {
    if (!value) {
        return '-';
    }
    const date = moment(value);
    return date.isValid() ? date.format('DD MMM YYYY') : '-';
};

const getTrailingDateRange = (days: number): [Moment, Moment] => {
    const today = moment();
    return [today.clone().subtract(days - 1, 'days'), today];
};
const getDefaultDateRange = () => getTrailingDateRange(30);

const getMonthDateRange = (today: string, monthsAgo: number): [Moment, Moment] => {
    const month = moment(today).startOf('month').subtract(monthsAgo, 'months');
    return [month, monthsAgo === 0 ? moment(today) : month.clone().endOf('month')];
};

const getQueryDateRange = (dateRange: [Moment, Moment], utcDates = false) => {
    const [start, end] = utcDates ? dateRange.map((date) => moment.utc(date.format('YYYY-MM-DD'))) : dateRange;
    return {
        startDate: start.clone().startOf('day').toISOString(),
        endDate: end.clone().endOf('day').toISOString(),
    };
};

const normalizeLineItems = (items?: LineItem[], root = false): NormalizedLineGroup => {
    const safeItems = Array.isArray(items) ? items : [];
    const dates = new Set<string>();

    safeItems.forEach((item) => {
        const points = Array.isArray(item?.points) ? item.points : [];
        points.forEach((point) => {
            if (point?.date && moment(point.date, 'YYYY-MM-DD', true).isValid()) {
                dates.add(point.date);
            }
        });
    });

    const rows = Array.from(dates)
        .sort()
        .map((date) => ({ date }));
    const rowsByDate = new Map(rows.map((row) => [row.date, row]));
    const series = safeItems.map((item, index) => {
        const dataKey = getSeriesKey(item?.key || item?.label, index);
        const points = Array.isArray(item?.points) ? item.points : [];

        points.forEach((point) => {
            if (!point?.date) {
                return;
            }
            const row = rowsByDate.get(point.date);
            if (row) {
                row[dataKey] = safeNumber(point.value);
            }
        });

        return {
            dataKey,
            name: getMetricLabel(item, `Series ${index + 1}`),
            stroke: palette[index % palette.length],
            missingValue: root && item.key === 'active_users' ? null : 0,
        };
    });

    rows.forEach((row) => {
        series.forEach((item) => {
            if (typeof row[item.dataKey] === 'undefined') {
                row[item.dataKey] = item.missingValue;
            }
        });
    });

    return { rows, series };
};

const getChartMax = (rows: any[], series: SeriesConfig[]) => {
    const maxValue = rows.reduce((max, row) => {
        const rowMax = series.reduce((currentMax, item) => Math.max(currentMax, safeNumber(row[item.dataKey])), 0);
        return Math.max(max, rowMax);
    }, 0);
    return maxValue > 0 ? Math.ceil(maxValue * 1.1) : 1;
};

const getMetricCards = (data?: UserStatisticsResponse) => {
    const summary = Array.isArray(data?.charts?.summary) ? data?.charts?.summary || [] : [];
    const items: ValueItem[] =
        summary.length > 0 ? summary : Object.entries(data?.metrics || {}).map(([key, value]) => ({ key, value }));
    return items.filter(isVisibleMetric);
};

const LineChartCard = ({ rows, series, root = false }: NormalizedLineGroup & { root?: boolean }) => {
    const [opacity, setOpacity] = useState<any>({});
    const maxValue = useMemo(() => getChartMax(rows, series), [rows, series]);

    const handleMouseEnter = (item: any) => {
        if (item?.dataKey) {
            setOpacity((state) => ({ ...state, [item.dataKey]: 0.45 }));
        }
    };

    const handleMouseLeave = (item: any) => {
        if (item?.dataKey) {
            setOpacity((state) => ({ ...state, [item.dataKey]: 1 }));
        }
    };

    if (series.length === 0 || rows.length === 0) {
        return (
            <div className="user-insights-empty">
                <Empty description="No data" />
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={360} minWidth={root ? 480 : undefined}>
            <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#f0f0f0" vertical={false} />
                <Legend verticalAlign="top" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
                <YAxis allowDecimals={false} tickCount={5} domain={[0, maxValue]} />
                <XAxis
                    dataKey="date"
                    minTickGap={20}
                    interval={root ? 'preserveStartEnd' : undefined}
                    padding={root ? { left: 12, right: 12 } : undefined}
                    tickFormatter={(value) => moment(value).format('DD MMM')}
                />
                <Tooltip
                    labelFormatter={(label) => moment(label).format('DD MMM YYYY')}
                    formatter={(value: any, name: any) => [formatNumber(value), name]}
                />
                {series.map((item) => (
                    <Line
                        key={item.dataKey}
                        name={item.name}
                        dot={false}
                        type="monotone"
                        dataKey={item.dataKey}
                        stroke={item.stroke}
                        strokeWidth={2}
                        opacity={opacity[item.dataKey] ?? 1}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

const UserInsightsComponent = () => {
    const rootStatistics = useRootStatistics();
    const [dateRange, setDateRange] = useState<[Moment, Moment]>(getDefaultDateRange);
    const [rootMonthsAgo, setRootMonthsAgo] = useState(0);
    const today = moment().format('YYYY-MM-DD');
    const rootDateOptions = useMemo(
        () =>
            [0, 1, 2].map((monthsAgo) => ({
                value: monthsAgo,
                label: getMonthDateRange(today, monthsAgo)[0].format('MMMM YYYY'),
            })),
        [today],
    );
    const queryDateRange = useMemo(
        () => getQueryDateRange(rootStatistics ? getMonthDateRange(today, rootMonthsAgo) : dateRange, rootStatistics),
        [dateRange, rootStatistics, rootMonthsAgo, today],
    );
    const needsSignupHistory = rootStatistics;
    const signupHistoryStart = moment
        .min(moment.utc(ROOT_ACTIVE_SIGNUPS_START), moment.utc(queryDateRange.startDate))
        .startOf('day')
        .toISOString();
    const {
        data: signupHistory,
        isError: signupHistoryError,
        isFetching: signupHistoryFetching,
    } = useQuery<UserStatisticsResponse>(
        ['rootActiveUserSignups', signupHistoryStart, queryDateRange.endDate],
        ApiClient.getUserStatistics,
        { enabled: needsSignupHistory, refetchOnWindowFocus: false },
    );
    const {
        data: rawData,
        isError,
        isFetching,
    } = useQuery<UserStatisticsResponse>(
        ['userStatistics', queryDateRange.startDate, queryDateRange.endDate],
        ApiClient.getUserStatistics,
        {
            refetchOnWindowFocus: false,
        },
    );
    const activeTrend = useMemo(
        () => buildRootActiveUserTrend(queryDateRange, signupHistory),
        [queryDateRange, signupHistory],
    );
    const data = useMemo(
        () => getUserStatisticsView(rawData, rootStatistics, activeTrend, queryDateRange),
        [rawData, rootStatistics, activeTrend, queryDateRange],
    );
    const metricCards = useMemo(() => getMetricCards(data), [data]);
    const normalizedLines = useMemo(() => {
        return lineGroups.map((group) => ({
            ...group,
            chart: normalizeLineItems(data?.charts?.line?.[group.key]?.filter(isVisibleMetric), rootStatistics),
        }));
    }, [data, rootStatistics]);

    return (
        <DrawerLayout>
            <UserInsightsWrapper>
                <div className="user-insights-toolbar">
                    <div className="user-insights-title">User Insights</div>
                    {rootStatistics ? (
                        <Select
                            className="user-insights-range-select"
                            aria-label="User insights date range"
                            value={rootMonthsAgo}
                            options={rootDateOptions}
                            onChange={setRootMonthsAgo}
                        />
                    ) : (
                        <RangePicker
                            allowClear={false}
                            value={dateRange}
                            onChange={(values) => {
                                if (values && values[0] && values[1]) {
                                    setDateRange([values[0], values[1]]);
                                }
                            }}
                        />
                    )}
                </div>

                {isError ? (
                    <Alert
                        className="user-insights-error"
                        type="error"
                        showIcon
                        message="Unable to load user insights"
                    />
                ) : null}

                {needsSignupHistory && !signupHistoryFetching && (signupHistoryError || activeTrend.value === null) ? (
                    <Alert
                        className="user-insights-error"
                        type="warning"
                        showIcon
                        message="Active-user trend unavailable: signup history could not be loaded"
                    />
                ) : null}

                {(isFetching && !data) || (needsSignupHistory && signupHistoryFetching && !signupHistory) ? (
                    <div className="user-insights-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        {!rootStatistics && (
                            <div className="user-insights-meta">
                                <div className="user-insights-meta-item">
                                    <div className="user-insights-meta-label">Selected range</div>
                                    <div className="user-insights-meta-value">
                                        {formatDate(data?.dateRange?.startDate)} -{' '}
                                        {formatDate(data?.dateRange?.endDate)}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="user-insights-summary-grid">
                            {metricCards.map((item) => (
                                <div className="user-insights-summary-card" key={item.key || item.label}>
                                    <div
                                        className="user-insights-summary-label"
                                        title={
                                            rootStatistics && getMetricLabel(item) === 'Active Users'
                                                ? rootActiveDescription
                                                : undefined
                                        }
                                    >
                                        {getMetricLabel(item)}
                                    </div>
                                    <div className="user-insights-summary-value">
                                        {rootStatistics && item.value == null ? '-' : formatNumber(item.value)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {normalizedLines.map((group) => (
                            <section
                                className={`user-insights-chart-section${
                                    rootStatistics ? ' user-insights-root-chart' : ''
                                }`}
                                aria-labelledby={`user-insights-${group.key}-title`}
                                tabIndex={rootStatistics ? 0 : undefined}
                                title={rootStatistics && group.key === 'summary' ? rootActiveDescription : undefined}
                                key={group.key}
                            >
                                <h2 className="user-insights-card-title" id={`user-insights-${group.key}-title`}>
                                    {group.label}
                                </h2>
                                <LineChartCard
                                    rows={group.chart.rows}
                                    series={group.chart.series}
                                    root={rootStatistics}
                                />
                            </section>
                        ))}
                    </>
                )}
            </UserInsightsWrapper>
        </DrawerLayout>
    );
};

export default UserInsightsComponent;
